import { createTheme, ThemeProvider } from "@mui/material/styles";
import axios, { AxiosError } from "axios";
import React, { createContext, useState } from "react";
import { useHistory } from "react-router-dom";
const shortHash = require("short-hash");

interface IPaymentMethods {
  creditCard: boolean;
  bankslip: boolean;
  pix: boolean;
}

interface OptionalFields {
  profession: boolean;
  exposed: boolean;
  salary: boolean;
}

interface Beneficiaries {
  name: string;
  kinship: string;
  percentageToReceive: string;
}
export interface IDependents {
  cpf: string;
  name: string;
  birthday: string;
  sex: string;
  cellphone: string;
}

export const AppContext = createContext({} as any);
const AppProvider: React.FC = ({ children }) => {
  const history = useHistory();

  const [subAccountId, setSubAccountId] = useState("");
  const [logo, setLogo] = useState(
    "https://bria-assets.s3.sa-east-1.amazonaws.com/logos+checkout/logo_bria.png"
  );
  const [lead, setLead] = useState<any>({ name: "" });
  const [paymentData, setPaymentData] = useState<any>({});
  const [plan, setPlan] = useState<any>();
  const [accountTheme, setAccountTheme] = useState<any>();
  const font = "'Exo 2', sans-serif";
  const [loading, setLoading] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState<string>("");
  const [acceptablePaymentMethods, setAcceptablePaymentMethods] =
    useState<IPaymentMethods>({
      creditCard: true,
      bankslip: false,
      pix: false,
    });
  const [optionalFields, setOptionalFields] = useState<OptionalFields>({
    profession: true,
    exposed: true,
    salary: true,
  });
  const [addBeneficiaries, setAddBeneficiaries] = useState(false);
  const [showUserCard, setShowUserCard] = useState(true);
  const [beneficiaries, setBeneficiaries] = useState<Beneficiaries[]>(
    [] as Array<Beneficiaries>
  );
  const [totalPercentage, setTotalPercentage] = useState(0.0);
  const [codeValidation, setCodeValidation] = useState(false);
  const [addDependents, setAddDependents] = useState(false);
  const [qtyOfDependents, setQtyOfDependents] = useState(0);
  const [onlyBySeller, setOnlyBySeller] = useState(false);
  const [sellerCode, setSellerCode] = useState("");
  const [contact, setContact] = useState("");
  const [documents, setDocuments] = useState("");
  const [finalSentence, setFinalSentence] = useState("");
  const [maxAge, setMaxAge] = useState(80);
  const [minAge, setMinAge] = useState(18);

  const getLead = async (leadId: string) => {
    setLoading(true);
    try {
      const response = await axios({
        method: "get",
        url: `${process.env.REACT_APP_API}/leads/${leadId}`,
      });
      setLoading(false);
      if (response.data) {
        setLead(response.data);
      }
      return response.data;
    } catch (e: any) {
      setLoading(false);
      return false;
    }
  };

  const updateLead = async (data: any) => {
    const response = await axios({
      method: "put",
      url: `${process.env.REACT_APP_API}/leads/${lead.leadId}`,
      headers: { "Content-Type": "application/json" },
      data: data,
    });

    if (response.status === 200) {
      setLead({
        ...lead,
        ...data,
      });
      return true;
    }
    return false;
  };

  const sendDependents = async (data: IDependents[]) => {
    const response = await axios({
      method: "put",
      url: `${process.env.REACT_APP_API}/leads/dependents/${lead.leadId}`,
      headers: { "Content-Type": "application/json" },
      data: data,
    });

    if (response.status === 200) {
      const newLead = {
        ...lead,
        dependents: data,
      };
      setLead(newLead);
      return true;
    }
    return false;
  };

  const createLead = async (data: any) => {
    const dataSubaccountId = {
      ...data,
      subAccountId: subAccountId,
      seller_code: sellerCode,
    };
    const response = await axios({
      method: "post",
      url: `${process.env.REACT_APP_API}/leads`,
      headers: { "Content-Type": "application/json" },
      data: dataSubaccountId,
    });
    if (response.data.leadId) {
      const newlead = {
        ...dataSubaccountId,
        leadId: response.data.leadId,
      };
      setLead(newlead);
      return true;
    }
    return false;
  };

  const checkout = async (data: any) => {
    data.sendSMS = false;
    if (plan && plan.sendSMSCheckout) {
      data.sendSMS = true;
    }
    try {
      setLoading(true);
      updateEvent({ type: "SQL_PGTO", leadId: lead.leadId });
      updateLead({ sql_payment: true });
      let endpoint = "checkout";
      if (plan && plan.checkoutWithProduct) {
        endpoint = "checkout-product";
      }
      const response = await axios({
        method: "post",
        url: `${process.env.REACT_APP_API}/${endpoint}`,
        headers: { "Content-Type": "application/json" },
        data: data,
      });
      setLoading(false);
      return response.data;
    } catch (e) {
      setLoading(false);
      return false;
    }
  };

  const subscribed = async (id: any) => {
    try {
      const response = await axios({
        method: "put",
        url: `${process.env.REACT_APP_API}/leads/subscribed/${id}`,
        headers: { "Content-Type": "application/json" },
      });
      return response.data;
    } catch (e) {
      return false;
    }
  };

  const getThemeAndPlans = async (id: string) => {
    try {
      const response = await axios({
        method: "get",
        url: `${process.env.REACT_APP_API}/products/${id}`,
      });
      if (response) {
        const responsePlan = {
          plan_selected: response.data.productName,
          product_description: response.data.productDescription,
          price: response.data.productValue,
          plans_description: response.data.productDetails,
          acceptDependents: response.data.acceptDependents || false,
          numberDependents: response.data.numberDependents || 0,
          validEmail: response.data.validEmail,
          checkoutWithProduct: response.data.checkoutWithProduct,
          sendSMSCheckout: response.data.sendSMSCheckout || false,
          preferredNameOption: response.data.preferredNameOption || false,
        };
        const responseTheme = {
          palette: {
            primary: {
              main: response.data.productColors[0].productColorMain,
            },
            secondary: {
              main: response.data.productColors[1].productColorMain,
            },
            background: {
              default: response.data.backgroundColor,
            },
          },
          typography: {
            fontFamily:
              response.data.subAccountId === "62a22cacd9e29a9f6de4d733"
                ? font
                : "",
          },
          subAccountLogoWidth: response.data.subAccountLogoWidth || "110px",
        };

        setAcceptablePaymentMethods(response.data.acceptablePaymentMethods);
        setBackgroundImage(response.data.backgroundImage);
        setSubAccountId(response.data.subAccountId);
        setPlan(responsePlan);
        setAccountTheme(responseTheme);
        setLogo(response.data.subAccountLogo);
        updateEvent({
          type: "ACESSO",
          subAccountId: response.data.subAccountId,
        });
        if (response.data.optionalFields)
          setOptionalFields(response.data.optionalFields);
        if (response.data.addBeneficiaries)
          setAddBeneficiaries(response.data.addBeneficiaries);
        if (response.data.userCard) setShowUserCard(response.data.userCard);
        if (response.data.codeValidation)
          setCodeValidation(response.data.codeValidation);
        if (response.data.codeValidation)
          setAddDependents(response.data.addDependents);
        if (response.data.codeValidation)
          setQtyOfDependents(+response.data.addDependents);
        if (response.data.contact) setContact(response.data.contact);
        if (response.data.documents) setDocuments(response.data.documents);
        if (response.data.finalSentence)
          setFinalSentence(response.data.finalSentence);
        if (response.data.onlyBySeller) {
          setOnlyBySeller(response.data.onlyBySeller);
          return response.data.onlyBySeller;
        }
      }
    } catch (e) {
      const responsePlan = {
        plan_selected: "deafult",
        product_description: "",
        price: "00,0",
        plans_description: [
          {
            productDetailName: "Auxilio Funeral",
            productDetailDescrition: "",
            productDetailIconName: "string",
            productDetailIconUrl:
              "https://bria-assets.s3.sa-east-1.amazonaws.com/icons_checkout/cicle.svg",
          },
          {
            productDetailName: "Telemedicina",
            productDetailDescrition: "",
            productDetailIconName: "",
            productDetailIconUrl:
              "https://bria-assets.s3.sa-east-1.amazonaws.com/icons_checkout/message_plus.svg",
          },
        ],
      };

      const responseTheme = {
        palette: {
          primary: {
            main: "#2f054d",
          },
          secondary: {
            main: "#f88675",
          },
          background: {
            default: "#f5f5f5",
          },
        },
      };
      setPlan(responsePlan);
      setAccountTheme(responseTheme);
    }
  };

  const updatePaymentData = (data: any) => {
    setPaymentData({
      ...paymentData,
      data,
    });
  };
  const updateEvent = async (data: any) => {
    try {
      const dataSubaccountId =
        data.subAccountId === ""
          ? {
              ...data,
              subAccountId: subAccountId,
            }
          : data;

      const response = await axios({
        method: "post",
        url: `${process.env.REACT_APP_API}/events`,
        headers: { "Content-Type": "application/json" },
        data: dataSubaccountId,
      });
      return response.data.eventId;
    } catch (e) {
      console.log(e);
    }
  };
  const bankslipPayment = async () => {
    if (lead.leadId) {
      setLoading(true);
      let attempts = 0;
      let createBankslip = true;
      let response: boolean = false;
      updateEvent({ type: "SQL_PGTO", leadId: lead.leadId });
      updateLead({ sql_payment: true });
      while (createBankslip && attempts < 10) {
        try {
          const res = await axios({
            method: "get",
            url: `${process.env.REACT_APP_API}/bankslip/${lead.leadId}`,
            headers: { "Content-Type": "application/json" },
            timeout: 1_000 * 30,
          });

          response = res.data.success;
          createBankslip = false;
        } catch (e) {
          if (axios.isAxiosError(e) && attempts < 5) {
            const axiosError = e as AxiosError;
            if (axiosError.code === "ECONNABORTED") {
              attempts += 1;
              console.log(
                "Bankslip sem resposta - tentativa: " + attempts.toString()
              );
              continue;
            } else {
              createBankslip = false;
            }
          }
        }
        setLoading(false);
        return response;
      }
    }
  };

  const addShortId = async () => {
    if (lead) {
      setLoading(true);
      const shortId = shortHash(lead.leadId);
      await updateLead({ shortId });
      setLoading(false);
    }
  };
  const sendEmailValidation = async () => {
    if (lead) {
      await addShortId();
      try {
        setLoading(true);
        await axios({
          method: "get",
          url: `${process.env.REACT_APP_API}/leads/sendvalidationemail/${lead.leadId}`,
        });
        updateEvent({ type: "VALIDATION_EMAIL_SENT", leadId: lead.leadId });
        setLoading(false);
        return true;
      } catch (e: any) {
        setLoading(false);
        return false;
      }
    }
  };

  const getLeadByShortId = async (shortId: string) => {
    try {
      setLoading(true);
      const response = await axios({
        method: "get",
        url: `${process.env.REACT_APP_API}/leads/getleadbyshortid/${shortId}`,
      });
      setLoading(false);
      const dataLead = { ...response.data, leadId: response.data._id };
      setLead(dataLead);
      return dataLead;
    } catch (e: any) {
      setLoading(false);
    }
  };
  const theme = createTheme(accountTheme);

  const calcTotalOfPercentage = (percentage: number) => {
    const total = totalPercentage + percentage;
    if (total > 100) {
      return false;
    } else {
      setTotalPercentage(total);
      return true;
    }
  };

  const GetAndValidSeller = async (code: string) => {
    const response = await axios({
      method: "get",
      url: `${process.env.REACT_APP_API}/sellers/code/${code}`,
    });
    if (response.status === 200) {
      setSellerCode(response.data.sellerCode);
      return true;
    } else {
      return false;
    }
  };
  return (
    <ThemeProvider theme={theme}>
      <AppContext.Provider
        value={{
          lead,
          theme,
          getThemeAndPlans,
          getLead,
          updateLead,
          createLead,
          checkout,
          subscribed,
          plan,
          logo,
          paymentData,
          updatePaymentData,
          updateEvent,
          accountTheme,
          bankslipPayment,
          setLoading,
          loading,
          sendEmailValidation,
          getLeadByShortId,
          backgroundImage,
          subAccountId,
          acceptablePaymentMethods,
          optionalFields,
          showUserCard,
          addBeneficiaries,
          beneficiaries,
          setBeneficiaries,
          totalPercentage,
          calcTotalOfPercentage,
          setTotalPercentage,
          codeValidation,
          addDependents,
          qtyOfDependents,
          onlyBySeller,
          GetAndValidSeller,
          contact,
          documents,
          finalSentence,
          maxAge,
          minAge,
          setLead,
          sendDependents,
        }}
      >
        {children}
      </AppContext.Provider>
    </ThemeProvider>
  );
};

export default AppProvider;
